/**
 * ShapeRegistry — per-Sheet catalog of registered shapes.
 *
 * Holds shapes by id so the backend adapter / facade registry can
 * resolve a shape at runtime. Registration is one-shot at sheet
 * construction; shapes are not removed.
 */

import type { Option } from "./option.ts";
import type { CellValue } from "./dtype.ts";
import type { Json } from "./json.ts";
import type { Shape, ShapeKey } from "./shape.ts";

/**
 * A sheet's catalog of cell shapes by id, which the backend adapter and the facade registry resolve
 * shapes through.
 */
export class ShapeRegistry {
  readonly #byId = new Map<string, Shape<Json | CellValue>>();

  register<C extends Json | CellValue>(shape: Shape<C>): void {
    if (this.#byId.has(shape.key.id)) {
      throw new Error(
        `ShapeRegistry: shape "${shape.key.id}" is already registered`,
      );
    }
    this.#byId.set(shape.key.id, shape);
  }

  /** Resolve by key. Returns null if no shape is registered. */
  get<C extends Json | CellValue>(key: ShapeKey<C>): Option<Shape<C>> {
    const found = this.#byId.get(key.id);
    // `register` stored the shape under its own key, so the key's phantom
    // type is the shape's `C`.
    return found ? (found as Shape<C>) : null;
  }

  /** Resolve by id string (for dynamic lookups / diagnostics). */
  getById(id: string): Option<Shape<Json | CellValue>> {
    return this.#byId.get(id) ?? null;
  }

  has(id: string): boolean {
    return this.#byId.has(id);
  }

  size(): number {
    return this.#byId.size;
  }
}
